// src/pages/LeaveRequestMaster.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import {
    Plus,
    Edit,
    RefreshCw,
    Eye,
    CheckCircle,
    XCircle,
    Search,
    ChevronDown,
    Check,
    X,
    Clock,
    Calendar,
    User,
    FileText,
    Info,
    AlertCircle,
    Filter,
    Sliders,
    ChevronLeft,
    ChevronRight,
    MoreVertical,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from "../../Components/Common/TableContainer";
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// ============================================
// TYPES
// ============================================

interface Employee {
    id: number;
    name: string;
    employee_code: string;
    designation?: {
        name: string;
    };
}

interface ApprovedBy {
    id: number;
    username: string;
    email: string;
}

interface LeaveTypeMaster {
    id: number;
    name: string;
    short_name: string | null;
    color_hex: string | null;
}

interface LeaveRequest {
    id: number;
    employee_id: number;
    leave_type: number;
    is_paid: boolean;
    start_date: string;
    end_date: string;
    number_of_days: number | string;
    reason: string;
    status: number;
    approved_by_id: number | null;
    approved_at: string | null;
    rejection_reason: string | null;
    half_day_type: number | null;
    attachment_url: string | null;
    requested_at: string;
    created_at: string;
    updated_at: string;
    employee: Employee;
    approved_by: ApprovedBy | null;
    leaveType: LeaveTypeMaster | null;
}

interface LeaveType {
    value: number;
    label: string;
}

interface LeaveStatus {
    value: number;
    label: string;
    color: string;
    icon: React.ReactNode;
}

// ============================================
// CONSTANTS
// ============================================

const LEAVE_TYPES: LeaveType[] = [
    { value: 0, label: 'Annual Leave' },
    { value: 1, label: 'Sick Leave' },
    { value: 2, label: 'Casual Leave' },
    { value: 3, label: 'Maternity Leave' },
    { value: 4, label: 'Paternity Leave' },
    { value: 5, label: 'Emergency Leave' },
    { value: 6, label: 'Unpaid Leave' },
    { value: 7, label: 'Other' },
];

const LEAVE_STATUSES: LeaveStatus[] = [
    { value: 0, label: 'Pending', color: 'warning', icon: <Clock size={14} /> },
    { value: 1, label: 'Approved', color: 'success', icon: <CheckCircle size={14} /> },
    { value: 2, label: 'Rejected', color: 'danger', icon: <XCircle size={14} /> },
    { value: 3, label: 'Cancelled', color: 'secondary', icon: <X size={14} /> },
];

const HALF_DAY_TYPES = [
    { value: 0, label: 'First Half' },
    { value: 1, label: 'Second Half' },
];

// ============================================
// ACTION DROPDOWN COMPONENT
// ============================================

interface ActionDropdownProps {
    leave: LeaveRequest;
    onView: (leave: LeaveRequest) => void;
    onEdit: (leave: LeaveRequest) => void;
    onApprove: (leave: LeaveRequest) => void;
    onReject: (leave: LeaveRequest) => void;
    onCancel: (leave: LeaveRequest) => void;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
    leave,
    onView,
    onEdit,
    onApprove,
    onReject,
    onCancel,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggle = () => setDropdownOpen(!dropdownOpen);

    const isPending = leave.status === 0;
    const isApproved = leave.status === 1;
    const isRejected = leave.status === 2;
    const isCancelled = leave.status === 3;

    return (
        <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
            <DropdownToggle
                tag="button"
                className="btn btn-secondary btn-sm d-flex align-items-center gap-1"
                size="sm"
            >
                Action <ChevronDown size={14} />
            </DropdownToggle>
            <DropdownMenu>
                <DropdownItem onClick={() => { toggle(); onView(leave); }} className="text-info">
                    <Eye size={14} className="me-2" />
                    View Details
                </DropdownItem>

                {isPending && (
                    <>
                        <DropdownItem onClick={() => { toggle(); onApprove(leave); }} className="text-success">
                            <Check size={14} className="me-2" />
                            Approve
                        </DropdownItem>
                        <DropdownItem onClick={() => { toggle(); onReject(leave); }} className="text-danger">
                            <X size={14} className="me-2" />
                            Reject
                        </DropdownItem>
                        <DropdownItem onClick={() => { toggle(); onEdit(leave); }} className="text-primary">
                            <Edit size={14} className="me-2" />
                            Edit
                        </DropdownItem>
                        <DropdownItem onClick={() => { toggle(); onCancel(leave); }} className="text-secondary">
                            <XCircle size={14} className="me-2" />
                            Cancel
                        </DropdownItem>
                    </>
                )}

                {isApproved && !isRejected && !isCancelled && (
                    <>
                        <DropdownItem onClick={() => { toggle(); onCancel(leave); }} className="text-secondary">
                            <XCircle size={14} className="me-2" />
                            Cancel
                        </DropdownItem>
                    </>
                )}

                {isRejected && (
                    <DropdownItem disabled className="text-muted">
                        <AlertCircle size={14} className="me-2" />
                        Already Rejected
                    </DropdownItem>
                )}

                {isCancelled && (
                    <DropdownItem disabled className="text-muted">
                        <XCircle size={14} className="me-2" />
                        Already Cancelled
                    </DropdownItem>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

// ============================================
// MAIN COMPONENT
// ============================================

const LeaveRequestMaster: React.FC = () => {
    // ==========================================
    // STATE
    // ==========================================

    const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        leave_type: '',
        status: '',
        month: '',
        year: new Date().getFullYear().toString(),
        employee_name: '',
        employee_code: '',
    });

    // View Modal
    const [viewModal, setViewModal] = useState(false);
    const [viewingLeave, setViewingLeave] = useState<LeaveRequest | null>(null);

    // Edit Modal
    const [editModal, setEditModal] = useState(false);
    const [editingLeave, setEditingLeave] = useState<LeaveRequest | null>(null);
    const [editFormData, setEditFormData] = useState({
        leave_type: '',
        start_date: '',
        end_date: '',
        reason: '',
        half_day_type: '',
    });
    const [submitting, setSubmitting] = useState(false);

    // Approve Modal
    const [approveModal, setApproveModal] = useState(false);
    const [approvingLeave, setApprovingLeave] = useState<LeaveRequest | null>(null);
    const [approveIsPaid, setApproveIsPaid] = useState<boolean>(true);        // ✅ NEW
    const [approveSubmitting, setApproveSubmitting] = useState(false);        // ✅ NEW (optional but recommended)

    // Reject Modal
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectingLeave, setRejectingLeave] = useState<LeaveRequest | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');

    // Cancel Modal
    const [cancelModal, setCancelModal] = useState(false);
    const [cancellingLeave, setCancellingLeave] = useState<LeaveRequest | null>(null);

    // Filter Dropdown
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);



    // ==========================================
    // FETCH DATA
    // ==========================================

    const fetchLeaveRequests = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                sortBy: 'created_at',
                sortOrder: 'desc',
            };

            // Apply filters
            if (filters.leave_type) {
                params.leave_type = parseInt(filters.leave_type);
            }
            if (filters.status) {
                params.status = parseInt(filters.status);
            }
            if (filters.month) {
                params.month = parseInt(filters.month);
            }
            if (filters.year) {
                params.year = parseInt(filters.year);
            }
            if (filters.employee_name) {
                params.employee_name = filters.employee_name;
            }
            if (filters.employee_code) {
                params.employee_code = filters.employee_code;
            }

            const response = await apiClient.get('/leave-requests', { params });
            console.log('leave request', response)
            const responseData = response;

            setLeaveRequests(responseData.data || []);
            setTotalCount(responseData.pagination?.total || 0);
        } catch (error: any) {
            console.error('Error fetching leave requests:', error);
            setError(error.message || 'Failed to fetch leave requests');
            toast.error('Failed to load leave requests');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filters]);

    useEffect(() => {
        fetchLeaveRequests();
    }, [fetchLeaveRequests]);

    // ==========================================
    // HANDLERS
    // ==========================================

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0);
    };

    const handleFilterChange = (field: string, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(0);
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            leave_type: '',
            status: '',
            month: '',
            year: new Date().getFullYear().toString(),
            employee_name: '',
            employee_code: '',
        });
        setCurrentPage(0);
    };

    // ==========================================
    // VIEW HANDLER
    // ==========================================

    const handleView = (leave: LeaveRequest) => {
        setViewingLeave(leave);
        setViewModal(true);
    };

    // ==========================================
    // EDIT HANDLERS
    // ==========================================

    const handleEdit = (leave: LeaveRequest) => {
        setEditingLeave(leave);
        setEditFormData({
            leave_type: leave.leave_type.toString(),
            start_date: format(new Date(leave.start_date), 'yyyy-MM-dd'),
            end_date: format(new Date(leave.end_date), 'yyyy-MM-dd'),
            reason: leave.reason,
            half_day_type: leave.half_day_type?.toString() || '',
        });
        setEditModal(true);
    };

    const handleEditSubmit = async () => {
        if (!editingLeave) return;

        setSubmitting(true);
        try {
            await apiClient.put(`/leave-requests/${editingLeave.id}`, {
                leave_type: parseInt(editFormData.leave_type),
                start_date: editFormData.start_date,
                end_date: editFormData.end_date,
                reason: editFormData.reason,
                half_day_type: editFormData.half_day_type ? parseInt(editFormData.half_day_type) : null,
            });

            toast.success('Leave request updated successfully!');
            setEditModal(false);
            fetchLeaveRequests();
        } catch (error: any) {
            console.error('Error updating leave request:', error);
            toast.error(error.message || 'Failed to update leave request');
        } finally {
            setSubmitting(false);
        }
    };

    // ==========================================
    // APPROVE HANDLERS
    // ==========================================

    const handleApprove = (leave: LeaveRequest) => {
        setApprovingLeave(leave);
        setApproveIsPaid(leave.is_paid);
        setApproveModal(true);
    };

    const confirmApprove = async () => {
        if (!approvingLeave) return;

        setApproveSubmitting(true);
        try {
            await apiClient.put(`/leave-requests/${approvingLeave.id}/status`, {
                status: 1,
                is_paid: approveIsPaid,
            });

            toast.success(
                approveIsPaid
                    ? 'Leave approved as Paid'
                    : 'Leave approved as Unpaid'
            );

            setApproveModal(false);
            setApprovingLeave(null);
            fetchLeaveRequests();
        } catch (error: any) {
            console.error('Error approving leave:', error);
            toast.error(error.message || 'Failed to approve leave request');
        } finally {
            setApproveSubmitting(false);
        }
    };

    // ==========================================
    // REJECT HANDLERS
    // ==========================================

    const handleReject = (leave: LeaveRequest) => {
        setRejectingLeave(leave);
        setRejectionReason('');
        setRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectingLeave) return;

        if (!rejectionReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        try {
            await apiClient.put(`/leave-requests/${rejectingLeave.id}/status`, {
                status: 2, // Rejected
                rejection_reason: rejectionReason.trim(),
            });

            toast.success('Leave request rejected');
            setRejectModal(false);
            fetchLeaveRequests();
        } catch (error: any) {
            console.error('Error rejecting leave:', error);
            toast.error(error.message || 'Failed to reject leave request');
        }
    };

    // ==========================================
    // CANCEL HANDLERS
    // ==========================================

    const handleCancel = (leave: LeaveRequest) => {
        setCancellingLeave(leave);
        setCancelModal(true);
    };

    const confirmCancel = async () => {
        if (!cancellingLeave) return;

        try {
            await apiClient.delete(`/leave-requests/${cancellingLeave.id}`);

            toast.success('Leave request cancelled successfully!');
            setCancelModal(false);
            fetchLeaveRequests();
        } catch (error: any) {
            console.error('Error cancelling leave:', error);
            toast.error(error.message || 'Failed to cancel leave request');
        }
    };

    // ==========================================
    // UTILITY FUNCTIONS
    // ==========================================

    // ✅ Simple: name comes straight from the row
    const getLeaveTypeName = (leave: LeaveRequest): string =>
        leave.leaveType?.name ?? 'Unknown';

    const getLeaveStatus = (value: number): LeaveStatus | undefined => {
        return LEAVE_STATUSES.find(s => s.value === value);
    };

    const getStatusBadge = (value: number) => {
        const status = getLeaveStatus(value);
        if (!status) return <Badge color="secondary">Unknown</Badge>;
        return (
            <Badge color={status.color}>
                {status.icon}
                <span className="ms-1">{status.label}</span>
            </Badge>
        );
    };

    const getHalfDayLabel = (value: number | null) => {
        if (value === null) return 'Full Day';
        const halfDay = HALF_DAY_TYPES.find(h => h.value === value);
        return halfDay ? halfDay.label : 'Unknown';
    };

    const formatDate = (dateString: string) => {
        try {
            return format(parseISO(dateString), 'dd MMM yyyy');
        } catch {
            return 'N/A';
        }
    };

    const formatDateTime = (dateString: string) => {
        try {
            return format(parseISO(dateString), 'dd MMM yyyy HH:mm');
        } catch {
            return 'N/A';
        }
    };

    const formatRelativeTime = (dateString: string) => {
        try {
            return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
        } catch {
            return 'N/A';
        }
    };

    // ==========================================
    // TABLE COLUMNS
    // ==========================================

    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const { row } = cell;
                    return <strong>{currentPage * pageSize + row.index + 1}</strong>;
                },
            },
            {
                header: 'Employee',
                accessorKey: 'employee',
                cell: (cell: any) => {
                    const employee = cell.getValue();
                    return (
                        <div>
                            <div className="fw-semibold">{employee?.name || 'N/A'}</div>
                            <div className="text-muted small">{employee?.employee_code || 'N/A'}</div>
                            {employee?.designation?.name && (
                                <div className="text-muted small">{employee.designation.name}</div>
                            )}
                        </div>
                    );
                },
            },
            {
                header: 'Leave Type',
                accessorKey: 'leaveType',
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const row: LeaveRequest = cell.row.original;
                    const lt = row.leaveType;
                    const hex = lt?.color_hex || '#6c757d';

                    return (
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '3px 10px',
                                    borderRadius: 999,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    // 15% opacity background from the same hex
                                    backgroundColor: `${hex}26`,
                                    // solid color text
                                    color: hex,
                                    border: `1px solid ${hex}40`,
                                }}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: hex,
                                    }}
                                />
                                {lt?.name ?? 'Unknown'}
                                {lt?.short_name && (
                                    <span style={{ opacity: 0.7 }}>
                                        ({lt.short_name})
                                    </span>
                                )}
                            </span>
                            {row.is_paid === false && (
                                <Badge color="secondary">Unpaid</Badge>
                            )}
                        </div>
                    );
                },
            },
            {
                header: 'Dates',
                accessorKey: 'start_date',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    return (
                        <div>
                            <div>{formatDate(row.start_date)}</div>
                            <div className="text-muted small">→ {formatDate(row.end_date)}</div>
                            <div className="text-muted small">
                                {row.number_of_days} day(s) • {getHalfDayLabel(row.half_day_type)}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Days',
                accessorKey: 'number_of_days',
                cell: (cell: any) => {
                    const value = cell.getValue();
                    return <Badge color="primary" pill>{value}</Badge>;
                },
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: (cell: any) => getStatusBadge(cell.getValue()),
            },
            {
                header: 'Requested',
                accessorKey: 'requested_at',
                cell: (cell: any) => (
                    <div>
                        <div>{formatDateTime(cell.getValue())}</div>
                        <div className="text-muted small">{formatRelativeTime(cell.getValue())}</div>
                    </div>
                ),
            },
            {
                header: 'Actions',
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const leave = cell.row.original;
                    return (
                        <ActionDropdown
                            leave={leave}
                            onView={handleView}
                            onEdit={handleEdit}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            onCancel={handleCancel}
                        />
                    );
                },
            },
        ],
        [currentPage, pageSize]
    );

    // ==========================================
    // RENDER
    // ==========================================

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Leave Request Master" pageTitle="Leave Management" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="card-title mb-0">Leave Requests</h5>
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    {/* Search */}
                                    <div className="position-relative" style={{ width: '200px' }}>
                                        <Input
                                            type="text"
                                            placeholder="Search employee..."
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            className="ps-4"
                                            bsSize="sm"
                                        />
                                        <Search
                                            size={14}
                                            className="position-absolute top-50 translate-middle-y text-muted"
                                            style={{ left: '10px' }}
                                        />
                                    </div>

                                    {/* Filter Dropdown */}
                                    <Dropdown
                                        isOpen={filterDropdownOpen}
                                        toggle={() => setFilterDropdownOpen(!filterDropdownOpen)}
                                    >
                                        <DropdownToggle
                                            tag="button"
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                        >
                                            <Filter size={14} />
                                            Filters
                                            {Object.values(filters).some(v => v) && (
                                                <Badge color="primary" pill className="ms-1">1</Badge>
                                            )}
                                        </DropdownToggle>
                                        <DropdownMenu style={{ width: '300px' }} className="p-3">
                                            <Form>
                                                <Row className="g-2">
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small">Leave Type</Label>
                                                            <Input
                                                                type="select"
                                                                value={filters.leave_type}
                                                                onChange={(e) => handleFilterChange('leave_type', e.target.value)}
                                                                bsSize="sm"
                                                            >
                                                                <option value="">All Types</option>
                                                                {LEAVE_TYPES.map(type => (
                                                                    <option key={type.value} value={type.value}>
                                                                        {type.label}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small">Status</Label>
                                                            <Input
                                                                type="select"
                                                                value={filters.status}
                                                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                                                bsSize="sm"
                                                            >
                                                                <option value="">All Status</option>
                                                                {LEAVE_STATUSES.map(status => (
                                                                    <option key={status.value} value={status.value}>
                                                                        {status.label}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small">Month</Label>
                                                            <Input
                                                                type="select"
                                                                value={filters.month}
                                                                onChange={(e) => handleFilterChange('month', e.target.value)}
                                                                bsSize="sm"
                                                            >
                                                                <option value="">All Months</option>
                                                                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                                                    <option key={month} value={month}>
                                                                        {format(new Date(2026, month - 1), 'MMMM')}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small">Year</Label>
                                                            <Input
                                                                type="select"
                                                                value={filters.year}
                                                                onChange={(e) => handleFilterChange('year', e.target.value)}
                                                                bsSize="sm"
                                                            >
                                                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                                                    <option key={year} value={year}>{year}</option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={12}>
                                                        <FormGroup>
                                                            <Label className="small">Employee Code</Label>
                                                            <Input
                                                                type="text"
                                                                value={filters.employee_code}
                                                                onChange={(e) => handleFilterChange('employee_code', e.target.value)}
                                                                bsSize="sm"
                                                                placeholder="Enter employee code"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={12}>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                className="w-100"
                                                                onClick={() => setFilterDropdownOpen(false)}
                                                            >
                                                                Apply Filters
                                                            </Button>
                                                            <Button
                                                                color="secondary"
                                                                size="sm"
                                                                className="w-100"
                                                                onClick={clearFilters}
                                                            >
                                                                Clear All
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        </DropdownMenu>
                                    </Dropdown>

                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={fetchLeaveRequests}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* Stats Summary */}
                                <Row className="mb-3">
                                    <Col md={3}>
                                        <div className="bg-light p-3 rounded">
                                            <div className="text-muted small">Total Requests</div>
                                            <h4 className="mb-0">{totalCount}</h4>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="bg-warning bg-opacity-10 p-3 rounded">
                                            <div className="text-muted small">Pending</div>
                                            <h4 className="mb-0 text-warning">
                                                {leaveRequests.filter(l => l.status === 0).length}
                                            </h4>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="bg-success bg-opacity-10 p-3 rounded">
                                            <div className="text-muted small">Approved</div>
                                            <h4 className="mb-0 text-success">
                                                {leaveRequests.filter(l => l.status === 1).length}
                                            </h4>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="bg-danger bg-opacity-10 p-3 rounded">
                                            <div className="text-muted small">Rejected</div>
                                            <h4 className="mb-0 text-danger">
                                                {leaveRequests.filter(l => l.status === 2).length}
                                            </h4>
                                        </div>
                                    </Col>
                                </Row>

                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <AlertCircle size={16} className="me-2" />
                                        {error}
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading leave requests...</span>
                                    </div>
                                ) : (
                                    <TableContainer
                                        columns={columns}
                                        data={leaveRequests}
                                        customPageSize={pageSize}
                                        tableClass="table-striped"
                                        theadClass="table-light"
                                        manualPagination={true}
                                        pageCount={Math.ceil(totalCount / pageSize)}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                        totalRecords={totalCount}
                                        showPageSizeOptions={true}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ===========================================
                VIEW MODAL
             ============================================ */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
                <ModalHeader toggle={() => setViewModal(false)}>
                    <div className="d-flex align-items-center gap-2">
                        <FileText size={20} />
                        Leave Request #{viewingLeave?.id}
                    </div>
                </ModalHeader>

                <ModalBody>
                    {viewingLeave && (
                        <>
                            {/* ---------- TOP: Employee + Status ---------- */}
                            <div className="d-flex justify-content-between align-items-start mb-4 pb-3 border-bottom">
                                <div>
                                    <div className="d-flex align-items-center gap-2">
                                        <div
                                            className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
                                            style={{ width: 40, height: 40 }}
                                        >
                                            <User size={18} className="text-primary" />
                                        </div>
                                        <div>
                                            <div className="fw-semibold">
                                                {viewingLeave.employee?.name || 'N/A'}
                                            </div>
                                            <div className="text-muted small">
                                                {viewingLeave.employee?.employee_code}
                                                {viewingLeave.employee?.designation?.name &&
                                                    ` • ${viewingLeave.employee.designation.name}`}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-end">
                                    {getStatusBadge(viewingLeave.status)}
                                </div>
                            </div>

                            {/* ---------- LEAVE SUMMARY CARD ---------- */}
                            <div className="bg-light rounded p-3 mb-3">
                                <Row className="g-3">
                                    {/* Leave Type */}
                                    <Col md={4}>
                                        <div className="text-muted small mb-1">Leave Type</div>
                                        <div className="d-flex align-items-center gap-2">
                                            {viewingLeave.leaveType?.color_hex && (
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: 3,
                                                        backgroundColor: viewingLeave.leaveType.color_hex,
                                                    }}
                                                />
                                            )}
                                            <span className="fw-semibold">
                                                {viewingLeave.leaveType?.name || 'Unknown'}
                                            </span>
                                            {viewingLeave.leaveType?.short_name && (
                                                <span className="text-muted small">
                                                    ({viewingLeave.leaveType.short_name})
                                                </span>
                                            )}
                                        </div>
                                    </Col>

                                    {/* Duration */}
                                    <Col md={4}>
                                        <div className="text-muted small mb-1">Duration</div>
                                        <div className="fw-semibold">
                                            {Number(viewingLeave.number_of_days)} day
                                            {Number(viewingLeave.number_of_days) !== 1 ? 's' : ''}
                                            {viewingLeave.half_day_type !== null && (
                                                <span className="text-muted small ms-2">
                                                    • {getHalfDayLabel(viewingLeave.half_day_type)}
                                                </span>
                                            )}
                                        </div>
                                    </Col>

                                    {/* Paid / Unpaid */}
                                    <Col md={4}>
                                        <div className="text-muted small mb-1">Payment</div>
                                        {viewingLeave.is_paid ? (
                                            <Badge color="primary">Paid</Badge>
                                        ) : (
                                            <Badge color="secondary">Unpaid</Badge>
                                        )}
                                    </Col>

                                    {/* Date Range */}
                                    <Col md={12}>
                                        <div className="text-muted small mb-1">Leave Period</div>
                                        <div className="fw-semibold">
                                            <Calendar size={14} className="me-1" />
                                            {formatDate(viewingLeave.start_date)}
                                            {viewingLeave.start_date !== viewingLeave.end_date && (
                                                <>
                                                    <span className="text-muted mx-2">→</span>
                                                    {formatDate(viewingLeave.end_date)}
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* ---------- REASON ---------- */}
                            <div className="mb-3">
                                <div className="text-muted small mb-1">
                                    <FileText size={12} className="me-1" />
                                    Reason
                                </div>
                                <div className="p-3 bg-light rounded">
                                    {viewingLeave.reason || (
                                        <span className="text-muted fst-italic">No reason provided</span>
                                    )}
                                </div>
                            </div>

                            {/* ---------- APPROVAL INFO (only when relevant) ---------- */}
                            {viewingLeave.status === 1 && viewingLeave.approved_by && (
                                <div className="p-3 bg-success bg-opacity-10 rounded mb-3">
                                    <div className="d-flex align-items-center gap-2 text-success fw-semibold mb-1">
                                        <CheckCircle size={14} />
                                        Approved
                                    </div>
                                    <div className="small text-muted">
                                        By <strong>{viewingLeave.approved_by.username}</strong>
                                        {viewingLeave.approved_at && (
                                            <> on {formatDateTime(viewingLeave.approved_at)}</>
                                        )}
                                    </div>
                                </div>
                            )}

                            {viewingLeave.status === 2 && (
                                <div className="p-3 bg-danger bg-opacity-10 rounded mb-3">
                                    <div className="d-flex align-items-center gap-2 text-danger fw-semibold mb-1">
                                        <XCircle size={14} />
                                        Rejected
                                    </div>
                                    {viewingLeave.rejection_reason && (
                                        <div className="small text-muted">
                                            Reason: {viewingLeave.rejection_reason}
                                        </div>
                                    )}
                                    {viewingLeave.approved_by && viewingLeave.approved_at && (
                                        <div className="small text-muted mt-1">
                                            By <strong>{viewingLeave.approved_by.username}</strong> on{' '}
                                            {formatDateTime(viewingLeave.approved_at)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {viewingLeave.status === 3 && (
                                <div className="p-3 bg-secondary bg-opacity-10 rounded mb-3">
                                    <div className="d-flex align-items-center gap-2 text-secondary fw-semibold">
                                        <XCircle size={14} />
                                        Cancelled
                                    </div>
                                </div>
                            )}

                            {/* ---------- FOOTER INFO: Requested / Attachment ---------- */}
                            <Row className="g-3">
                                <Col md={viewingLeave.attachment_url ? 6 : 12}>
                                    <div className="text-muted small mb-1">Requested</div>
                                    <div className="small">
                                        {formatDateTime(viewingLeave.requested_at)}
                                        <span className="text-muted ms-1">
                                            ({formatRelativeTime(viewingLeave.requested_at)})
                                        </span>
                                    </div>
                                </Col>

                                {viewingLeave.attachment_url && (
                                    <Col md={6}>
                                        <div className="text-muted small mb-1">Attachment</div>
                                        <a
                                            href={viewingLeave.attachment_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="small"
                                        >
                                            <FileText size={12} className="me-1" />
                                            View Attachment
                                        </a>
                                    </Col>
                                )}
                            </Row>
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={() => setViewModal(false)}>
                        Close
                    </Button>
                    {viewingLeave?.status === 0 && (
                        <>
                            <Button
                                color="success"
                                onClick={() => {
                                    setViewModal(false);
                                    handleApprove(viewingLeave);
                                }}
                            >
                                <Check size={14} className="me-1" />
                                Approve
                            </Button>
                            <Button
                                color="danger"
                                onClick={() => {
                                    setViewModal(false);
                                    handleReject(viewingLeave);
                                }}
                            >
                                <X size={14} className="me-1" />
                                Reject
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </Modal>

            {/* ===========================================
                EDIT MODAL
            ============================================ */}
            <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
                <ModalHeader toggle={() => setEditModal(false)}>
                    <Edit size={16} className="me-2" />
                    Edit Leave Request
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <Row className="g-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Leave Type *</Label>
                                    <Input
                                        type="select"
                                        value={editFormData.leave_type}
                                        onChange={(e) => setEditFormData(prev => ({ ...prev, leave_type: e.target.value }))}
                                        bsSize="sm"
                                    >
                                        <option value="">Select...</option>
                                        {LEAVE_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label-sm">Start Date *</Label>
                                    <Input
                                        type="date"
                                        value={editFormData.start_date}
                                        onChange={(e) => setEditFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                        bsSize="sm"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label-sm">End Date *</Label>
                                    <Input
                                        type="date"
                                        value={editFormData.end_date}
                                        onChange={(e) => setEditFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                        bsSize="sm"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label-sm">Half Day Type</Label>
                                    <Input
                                        type="select"
                                        value={editFormData.half_day_type}
                                        onChange={(e) => setEditFormData(prev => ({ ...prev, half_day_type: e.target.value }))}
                                        bsSize="sm"
                                    >
                                        <option value="">Full Day</option>
                                        {HALF_DAY_TYPES.map(h => (
                                            <option key={h.value} value={h.value}>
                                                {h.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Reason *</Label>
                                    <Input
                                        type="textarea"
                                        rows={3}
                                        value={editFormData.reason}
                                        onChange={(e) => setEditFormData(prev => ({ ...prev, reason: e.target.value }))}
                                        bsSize="sm"
                                        placeholder="Enter reason for leave"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setEditModal(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleEditSubmit} disabled={submitting}>
                        {submitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Updating...
                            </>
                        ) : (
                            'Update'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ===========================================
                APPROVE MODAL
            ============================================ */}
            {/* ===========================================
    APPROVE MODAL
============================================ */}
            <Modal
                isOpen={approveModal}
                toggle={() => !approveSubmitting && setApproveModal(false)}
                size="md"
            >
                <ModalHeader toggle={() => !approveSubmitting && setApproveModal(false)}>
                    <div className="d-flex align-items-center">
                        <CheckCircle size={18} className="me-2 text-success" />
                        <div>
                            <div className="fw-semibold">Approve Leave Request</div>
                            <small className="text-muted">
                                {approvingLeave?.employee?.name} • Request #{approvingLeave?.id}
                            </small>
                        </div>
                    </div>
                </ModalHeader>

                <ModalBody>
                    {approvingLeave && (
                        <>
                            {/* ── Leave Summary ── */}
                            <div className="p-3 bg-light rounded mb-3">
                                <Row className="g-2">
                                    <Col md={6}>
                                        <div className="text-muted small">Leave Type</div>
                                        <div className="fw-semibold">
                                            {approvingLeave.leaveType?.name || 'Unknown'}
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="text-muted small">Duration</div>
                                        <div className="fw-semibold">
                                            {Number(approvingLeave.number_of_days)} day(s)
                                            {approvingLeave.half_day_type !== null && (
                                                <> • {getHalfDayLabel(approvingLeave.half_day_type)}</>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <div className="text-muted small">Dates</div>
                                        <div className="fw-semibold">
                                            {formatDate(approvingLeave.start_date)}
                                            {approvingLeave.start_date !== approvingLeave.end_date && (
                                                <> → {formatDate(approvingLeave.end_date)}</>
                                            )}
                                        </div>
                                    </Col>
                                    {approvingLeave.reason && (
                                        <Col md={12}>
                                            <div className="text-muted small">Reason</div>
                                            <div className="small">{approvingLeave.reason}</div>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            {/* ── Paid / Unpaid Choice ── */}
                            <Label className="form-label-sm fw-semibold">
                                Payment for these days <span className="text-danger">*</span>
                            </Label>

                            <div className="d-flex flex-column gap-2 mb-3">
                                {/* PAID */}
                                <label
                                    className="p-3 rounded border d-flex align-items-start gap-2"
                                    style={{
                                        cursor: approveSubmitting ? 'not-allowed' : 'pointer',
                                        borderColor: approveIsPaid ? '#0d6efd' : '#dee2e6',
                                        borderWidth: approveIsPaid ? 2 : 1,
                                        backgroundColor: approveIsPaid ? '#e7f1ff' : '#fff',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="approve_paid"
                                        checked={approveIsPaid === true}
                                        onChange={() => setApproveIsPaid(true)}
                                        disabled={approveSubmitting}
                                        style={{ marginTop: 3 }}
                                    />
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                            <Badge color="primary">Paid Leave</Badge>
                                            <span>Employee receives full salary</span>
                                        </div>
                                        <small className="text-muted">
                                            These leave days will be counted as paid time off.
                                        </small>
                                    </div>
                                </label>

                                {/* UNPAID */}
                                <label
                                    className="p-3 rounded border d-flex align-items-start gap-2"
                                    style={{
                                        cursor: approveSubmitting ? 'not-allowed' : 'pointer',
                                        borderColor: approveIsPaid === false ? '#6c757d' : '#dee2e6',
                                        borderWidth: approveIsPaid === false ? 2 : 1,
                                        backgroundColor: approveIsPaid === false ? '#f1f3f5' : '#fff',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="approve_paid"
                                        checked={approveIsPaid === false}
                                        onChange={() => setApproveIsPaid(false)}
                                        disabled={approveSubmitting}
                                        style={{ marginTop: 3 }}
                                    />
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                            <Badge color="secondary">Unpaid Leave</Badge>
                                            <span>Salary will be deducted</span>
                                        </div>
                                        <small className="text-muted">
                                            These leave days will be treated as Loss of Pay.
                                        </small>
                                    </div>
                                </label>
                            </div>

                            {/* ── Change Hint ── */}
                            {approvingLeave.is_paid !== approveIsPaid ? (
                                <Alert color="warning" className="mb-0 py-2">
                                    <small>
                                        <AlertCircle size={12} className="me-1" />
                                        This will change the payment from{' '}
                                        <strong>{approvingLeave.is_paid ? 'Paid' : 'Unpaid'}</strong>{' '}
                                        to <strong>{approveIsPaid ? 'Paid' : 'Unpaid'}</strong>.
                                    </small>
                                </Alert>
                            ) : (
                                <Alert color="info" className="mb-0 py-2">
                                    <small>
                                        <Info size={12} className="me-1" />
                                        Approving will keep the current payment:{' '}
                                        <strong>{approveIsPaid ? 'Paid' : 'Unpaid'}</strong>.
                                    </small>
                                </Alert>
                            )}
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => setApproveModal(false)}
                        disabled={approveSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="success"
                        onClick={confirmApprove}
                        disabled={approveSubmitting || !approvingLeave}
                    >
                        {approveSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Approving...
                            </>
                        ) : (
                            <>
                                <Check size={14} className="me-1" />
                                Approve {approveIsPaid ? 'as Paid' : 'as Unpaid'}
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ===========================================
                REJECT MODAL
            ============================================ */}
            <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)}>
                <ModalHeader toggle={() => setRejectModal(false)}>
                    <XCircle size={16} className="me-2 text-danger" />
                    Reject Leave Request
                </ModalHeader>
                <ModalBody>
                    {rejectingLeave && (
                        <>
                            <Alert color="danger">
                                <XCircle size={16} className="me-2" />
                                Are you sure you want to reject this leave request?
                            </Alert>
                            <div className="mt-3">
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Employee:</span>
                                    <span className="fw-semibold">{rejectingLeave.employee?.name}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Leave Type:</span>
                                    <span>{(rejectingLeave.leaveType?.name)}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Duration:</span>
                                    <span>{rejectingLeave.number_of_days} day(s)</span>
                                </div>
                                {rejectingLeave.reason && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                        <div className="text-muted small">Reason:</div>
                                        <div>{rejectingLeave.reason}</div>
                                    </div>
                                )}
                            </div>
                            <FormGroup className="mt-3">
                                <Label className="form-label-sm">Rejection Reason *</Label>
                                <Input
                                    type="textarea"
                                    rows={3}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Please provide a reason for rejection"
                                    bsSize="sm"
                                />
                            </FormGroup>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setRejectModal(false)}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={confirmReject}>
                        <X size={14} className="me-1" />
                        Reject
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ===========================================
                CANCEL MODAL
            ============================================ */}
            <Modal isOpen={cancelModal} toggle={() => setCancelModal(false)}>
                <ModalHeader toggle={() => setCancelModal(false)}>
                    <XCircle size={16} className="me-2 text-secondary" />
                    Cancel Leave Request
                </ModalHeader>
                <ModalBody>
                    {cancellingLeave && (
                        <>
                            <Alert color="warning">
                                <AlertCircle size={16} className="me-2" />
                                Are you sure you want to cancel this leave request?
                            </Alert>
                            <div className="mt-3">
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Employee:</span>
                                    <span className="fw-semibold">{cancellingLeave.employee?.name}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Leave Type:</span>
                                    <span>{(cancellingLeave.leaveType?.name)}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Duration:</span>
                                    <span>{cancellingLeave.number_of_days} day(s)</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Dates:</span>
                                    <span>{formatDate(cancellingLeave.start_date)} → {formatDate(cancellingLeave.end_date)}</span>
                                </div>
                            </div>
                            <Alert color="info" className="mt-3">
                                This action will set the request status to "Cancelled".
                            </Alert>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setCancelModal(false)}>
                        Cancel
                    </Button>
                    <Button color="secondary" onClick={confirmCancel}>
                        <XCircle size={14} className="me-1" />
                        Confirm Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default LeaveRequestMaster;